import dayjs from 'dayjs'
import React from 'react'

import { TDateArg } from '@hozana/dates/types'

import { Cell } from 'elements/layout/Cell'
import { Div } from 'elements/layout/Div'
import { Row } from 'elements/layout/Row'
import { StretchCell } from 'elements/layout/StretchCell'
import { EllipsisMultiLines } from 'elements/text/EllipsisMultiLines'
import { P, TPProps } from 'elements/text/P'
import { TTextProps, Text } from 'elements/text/Text'

import { getUserRole } from 'modules/community/functions'
import { useCommunity } from 'modules/community/hooks/useCommunity'
import { UserAvatar } from 'modules/user/components/UserAvatar'
import { UserName } from 'modules/user/components/UserName'
import { useUser } from 'modules/user/hooks/useUser'

import { PUBLICATION_SOURCES } from '../constants'
import { PublicationHeaderDates } from './PublicationHeaderDates'

export type TStyledTextProps = TTextProps

export type TPublicationHeaderProps = {
  communityId: number
  showLinkToCommunity?: boolean
  date?: TDateArg
  source?: string
  rank?: number
} & TPProps

export const PublicationHeader: TFC<TPublicationHeaderProps> = ({
  communityId,
  date,
  showLinkToCommunity = true,
  source,
  rank,
}) => {
  const me = useUser()
  // use useCommunity instead of the publication.community object, to get up-to-date informations on CommunityPage
  const community = useCommunity({ communityId })
  const communityOwner = useUser({ userId: community.user })
  const formattedDate = dayjs(date).format('LL')
  const { hasRole } = getUserRole(community)

  const displayPublicationDate = source === PUBLICATION_SOURCES.COMMUNITY_PUBLICATIONS && (me.admin || hasRole)

  return (
    <Row align="middleLeft" gutter="10px" nowrap>
      <Cell>
        <Div w="60px">
          <UserAvatar userId={communityOwner.id} size="small" />
        </Div>
      </Cell>
      <StretchCell flex="1" align="left">
        <EllipsisMultiLines
          as="span"
          lines="3"
          fontSize={{ xs: '0.9em', sm: '1em' }}
          lineHeight="1.5em"
          p={{ xs: '0 30px 0 0', md: '0 35px 0 0' }}
        >
          {me.id === communityOwner.id ? (
            <>
              <Text firstUpper inline>
                trans:publication:word.you
              </Text>
              &nbsp;
            </>
          ) : (
            <>
              <UserName bold={false} userId={communityOwner.id} />
              &nbsp;
            </>
          )}
          {showLinkToCommunity && (
            <>
              <Text>trans:publication:word.in</Text>
              &nbsp;
              <Text>{community.name} </Text>
            </>
          )}
        </EllipsisMultiLines>
        {community.hasJoined && <PublicationHeaderDates community={community} />}
        {displayPublicationDate && (
          <P as="time" italic fontSize={{ xs: '0.8em', sm: '0.9em' }} lineHeight="1.5em" color="black30">
            N°{rank} - {formattedDate}
          </P>
        )}
      </StretchCell>
    </Row>
  )
}
